import RegionWarning from "@/components/PageEditor/editor-components/shared/RegionWarning";
import usePageEditorStore from "@/components/PageEditor/store/usePageEditorStore";
import { Element, useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { ReactNode } from "react";
import TagContainer from "../TagContainer";
import Settings from "./Settings";

interface PageContentProps {
    region: string;
    children: ReactNode;
}

const PageContentContainer = ({ region = "-1", children }: Partial<PageContentProps>) => {
    const activeRegion = usePageEditorStore((state) => state.activeRegion);
    const {
        id,
        connectors: { connect },
    } = useNode();

    const {
        enabled,
        query: { node },
    } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));

    const isFilterEnabled = node("PAGE_CONTENT").get().data.props.filters === "yes";
    const isTocEnabled = node("PAGE_CONTENT").get().data.props.toc === "yes";
    const isTagsEnabled = node("PAGE_CONTENT").get().data.props.tags === "yes";
    const isAnchorsEnabled = node("PAGE_CONTENT").get().data.props.anchors === "yes";
    const columnsCount = 1 + (isFilterEnabled || isAnchorsEnabled || isTocEnabled ? 1 : 0) + (isTagsEnabled ? 1 : 0);

    const shouldRender = enabled || region === "" || region === "-1" || region === activeRegion;

    if (enabled && !children)
        return (
            <>
                {isTagsEnabled && <Element id={`${id}-tag`} is={TagContainer} text="A" />}
                <div
                    ref={(ref) => connect(ref!)}
                    className={clsx([
                        "flex flex-col p-3 bg-gray-200 rounded animate-pulse",
                        columnsCount === 2 && "col-start-2",
                        columnsCount === 3 && "col-start-3",
                    ])}
                >
                    <RegionWarning region={region} enabled={enabled}>
                        This is an empty section container, start dropping elements here to get started!
                    </RegionWarning>
                </div>
            </>
        );

    if (!shouldRender) return null;

    return (
        <>
            {isTagsEnabled && <Element id={`${id}-tag`} is={TagContainer} text="A" />}
            <div
                ref={(ref) => connect(ref!)}
                className={clsx([
                    "flex flex-col gap-6",
                    columnsCount === 2 && "col-start-2",
                    columnsCount === 3 && "col-start-3",
                ])}
            >
                <RegionWarning region={region} enabled={enabled}>
                    {children}
                </RegionWarning>
            </div>
        </>
    );
};

export default PageContentContainer;

PageContentContainer.craft = {
    displayName: "Section",
    props: {
        region: "-1",
    },
    rules: {
        canDrag: () => true,
        canMoveOut: () => false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (targetNode: any) => targetNode.data.name === "PageContent",
    },
    related: {
        settings: Settings,
    },
};
